import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Menu, X } from "lucide-react";
import Logo from "@/components/Logo";

export default function MarketingNav() {
  const { user, logout } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // All in-nav anchor links live on the landing page (/); using an absolute
  // `/#anchor` guarantees they work from any route (ScrollToTop handles
  // scrolling to the hash after the landing page renders).
  const navLinks = [
    { href: "/#services", label: "Services" },
    { href: "/#our-work", label: "Our Work" },
    { href: "/#reviews", label: "Reviews" },
    { href: "/#about", label: "About" },
    { href: "/#quote", label: "Get a Quote" },
  ];

  return (
    <header
      data-testid="marketing-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-white/90 border-b border-stone-200/60 shadow-[0_2px_20px_rgb(0,0,0,0.04)]"
          : "backdrop-blur-md bg-white/60 border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-12 h-20 md:h-24 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 group" data-testid="nav-logo">
          <Logo size={72} />
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {navLinks.map((l) => (
            <Link
              key={l.href}
              to={l.href}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="px-4 py-2 text-sm font-medium text-stone-700 hover:text-brand-700 rounded-full hover:bg-stone-100 transition-colors"
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          {user && user.role === "admin" && (
            <>
              <button
                onClick={() => navigate("/admin/calendar")}
                data-testid="nav-dashboard-btn"
                className="px-5 py-2 text-sm font-semibold text-stone-700 hover:text-stone-900 rounded-full hover:bg-stone-100 transition-colors"
              >
                Admin
              </button>
              <button
                onClick={async () => { await logout(); navigate("/"); }}
                data-testid="nav-logout-btn"
                className="px-5 py-2 text-sm font-semibold text-white bg-stone-900 hover:bg-stone-800 rounded-full transition-all hover:-translate-y-0.5 shadow-md hover:shadow-lg"
              >
                Sign Out
              </button>
            </>
          )}
        </div>

        <button
          className="md:hidden p-2 rounded-full hover:bg-stone-100"
          onClick={() => setOpen(!open)}
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-stone-200 bg-white px-6 py-4 space-y-2" data-testid="nav-mobile-menu">
          {navLinks.map((l) => (
            <Link
              key={l.href}
              to={l.href}
              onClick={() => setOpen(false)}
              className="block px-4 py-3 text-base font-medium text-stone-700 hover:bg-stone-100 rounded-lg"
            >
              {l.label}
            </Link>
          ))}
          <div className="pt-3 border-t border-stone-200 flex flex-col gap-2">
            {user && user.role === "admin" ? (
              <>
                <Link to="/admin/calendar" className="px-4 py-3 text-center font-semibold rounded-full bg-stone-100" onClick={() => setOpen(false)}>
                  Admin
                </Link>
                <button
                  onClick={async () => { await logout(); navigate("/"); setOpen(false); }}
                  className="px-4 py-3 font-semibold rounded-full bg-stone-900 text-white"
                >
                  Sign Out
                </button>
              </>
            ) : null}
          </div>
        </div>
      )}
    </header>
  );
}
